import { useTranslateControlWithParameters } from "@/features/controls/hooks/use-translate-control-with-parameters";
import { useGetControlStatuses } from "@/spz-api/controls/controls";
import { useGetInvestigationConclusionCategories } from "@/spz-api/investigation-conclusions/investigation-conclusions";
import type {
  ControlResultListItemResponseDto,
  GetControlStatsType,
} from "@/spz-api/model";
import type { TreeSection } from "@spz/ui/components/tree-searchable-select";
import type { Column } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useLoaderData } from "react-router";
import { useControlResultListPaginated } from "../hooks/use-control-result-list-paginated";
import { useControlResultsBulkSelection } from "../hooks/use-control-results-bulk-selection";
import { useControlResultsPagination } from "../hooks/use-control-results-pagination";
import { useControlResultsSelection } from "../hooks/use-control-results-selection";
import { useControlResultsTable } from "../hooks/use-control-results-table";
import { useGetControlInformation } from "../hooks/use-get-control-information";
import { selectControlStatsViewModel } from "./select-control-stats.view-model";
export const selectControlResultsViewModel = (t: TFunction) => {
  const { controlId } = useLoaderData<{ controlId: string }>();
  const [selectedType, setType] = useState<GetControlStatsType>("volumes");

  // Control information
  const controlInformation = useGetControlInformation({ controlId, t });

  const controlName = useTranslateControlWithParameters(
    { name: controlInformation.name, currency: controlInformation.currency },
    controlInformation.parameters,
  );

  const { data: controlResultSteps } = useGetControlStatuses(controlId);

  const { data: investigationConclusionCategories } =
    useGetInvestigationConclusionCategories({
      query: {
        staleTime: 60 * 60 * 1000, // 1h
        gcTime: 24 * 60 * 60 * 1000, // keep cache across navigation
      },
    });

  const conclusionSections: TreeSection[] = useMemo(
    () =>
      investigationConclusionCategories?.data.map((category) => ({
        id: category.id,
        title: category.labelKeyName
          ? t(category.labelKeyName)
          : (category.name ?? ""),
        options: category.conclusions.map((conclusion) => ({
          label: conclusion.labelKeyName
            ? t(conclusion.labelKeyName)
            : (conclusion.name ?? ""),
          value: conclusion.id,
        })),
      })) ?? [],
    [investigationConclusionCategories?.data, t],
  );
  // Pagination
  const paginationModule = useControlResultsPagination();

  // Data fetching
  const controlResultListPaginated = useControlResultListPaginated({
    controlId,
    companyId: controlInformation.company.id,
    paginationParams: {
      pageSize: paginationModule.pageSize,
      page: paginationModule.currentPage,
    },
    controlResultColumns: controlInformation.controlResultColumns,
  });

  // Selection control result detail sheet, row click handler
  const controlResultsSelectionModule = useControlResultsSelection({
    controlId,
    controlInformation: {
      name: controlInformation.name,
      currency: controlInformation.currency,
      parameters: controlInformation.parameters,
      controlResultColumns: controlInformation.controlResultColumns,
    },
    companyId: controlInformation.company.id,
    loaded: controlResultListPaginated.loaded,
    getRowById: controlResultListPaginated.getRowById,
  });

  // Table (includes filters and dynamic filter components)
  const tableModule = useControlResultsTable({
    data: controlResultListPaginated.dataTableData,
    columns: controlResultListPaginated.columns as Column<
      ControlResultListItemResponseDto,
      unknown
    >[],
    rowSelection: controlResultsSelectionModule.rowSelection,
    setRowSelection: controlResultsSelectionModule.setRowSelection,
    pagination: paginationModule.pagination,
    setPagination: paginationModule.setPagination,
    totalCount: controlResultListPaginated.totalCount,
    controlResultColumns: controlInformation.controlResultColumns,
    t,
    currency: controlResultListPaginated.currency,
  });

  // Bulk Selection
  const bulkSelectionModule = useControlResultsBulkSelection({
    controlId,
    companyId: controlInformation.company.id,
    totalFilteredCount: controlResultListPaginated.totalCount,
    pageRowIds: tableModule.table.getRowModel().rows.map((r) => String(r.id)),
    controlResultColumns: controlInformation.controlResultColumns,
    controlResults: controlResultListPaginated.controlResults,
  });

  // Charts
  const controlStats = selectControlStatsViewModel(selectedType);

  return {
    // Control info
    controlId,
    controlName: controlName,
    controlDescription: controlInformation.description,
    analysisId: controlInformation.analysis.id,
    analysisName: controlInformation.analysis.name,
    periodName: controlInformation.period.name,
    company: controlInformation.company,

    // Table & Filters
    table: tableModule.table,
    dynamicFilters: tableModule.dynamicFilters,
    handleRowClick: controlResultsSelectionModule.handleRowClick,

    // Pagination
    pagination: {
      currentPage: paginationModule.currentPage,
      pageSize: paginationModule.pageSize,
      totalPages: controlResultListPaginated.totalPages,
      totalCount: controlResultListPaginated.totalCount,
    },

    // Charts
    charts: {
      loading: controlStats.stats.loading,
      error: controlStats.stats.error,
      population: controlStats.stats.data?.population,
      distributions: controlStats.stats.data?.distributions,
      frequencies: controlStats.stats.data?.frequencies,
      workflowStats: controlInformation.workflowStats,
    },
    selectedType,
    setType,

    // Loading & errors
    loading: controlResultListPaginated.loading,
    isLoading: controlResultListPaginated.loading,
    error: controlResultListPaginated.error,

    // Bulk Selection
    bulkSelection: {
      showBanner: bulkSelectionModule.showBanner,
      showAllSelectedBanner: bulkSelectionModule.showAllSelectedBanner,
      currentPageCount: bulkSelectionModule.currentPageCount,
      clearSelection: bulkSelectionModule.clearSelection,
      selectedCount: bulkSelectionModule.totalSelectedCount,
      selectAllAcrossFiltered: bulkSelectionModule.selectAllAcrossFiltered,
      handleOwnershipChange: bulkSelectionModule.handleOwnershipChange,
      canManageOwnership: bulkSelectionModule.canManageOwnership,
      isBulkActionInProgress: bulkSelectionModule.isBulkActionInProgress,
      selectionAnalysis: bulkSelectionModule.selectionAnalysis,
      filteredTransitions: bulkSelectionModule.filteredTransitions,
      handleTransitionSubmit: bulkSelectionModule.handleTransitionSubmit,
      canManagePriority: bulkSelectionModule.canManagePriority,
      handlePriorityChange: bulkSelectionModule.handlePriorityChange,
    },
    controlResultSteps: controlResultSteps?.data ?? [],
    conclusionSections,
  };
};
